import { FC } from "react";
import { Button, Grid } from "antd";
import classNames from "classnames";
import { MdError } from "react-icons/md";
import { MOBILE_BREAKPOINT_KEY } from "src/constants";
import InstructionCards from "../../InstructionCards/InstructionCards";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import {SuccessBannerIcon} from "src/assets/Icons";
import { FetchBaseQueryError } from "@reduxjs/toolkit/query";
import { SerializedError } from "@reduxjs/toolkit";
import { DiscountsItem } from "src/store/slices/item/subitems/discounts.types";
import { OrderItemResponse } from "src/store/slices/item/item.type";
import { RootState } from "src/store";
import { formatNumber } from "src/utility";
import ImageWithEmptyFallback from "src/components/ImageWithEmptyFallback/ImageWithEmptyFallback";

type Props = {
  data: DiscountsItem | undefined;
  imageUrl: string | undefined;
  onConfirm: () => void;
  onClose: () => void;
  loading: boolean;
  error: FetchBaseQueryError | SerializedError | undefined;
  orderData: OrderItemResponse | undefined;
  onRetry: () => void;
};

const DiscountsRedeemResult: FC<{
  data: DiscountsItem | undefined;
  error?: FetchBaseQueryError | SerializedError;
  onRetry: () => void;
  onClose: () => void;
}> = ({
  data,
  error,
  onRetry,
  onClose,
}) => {
  const { t } = useTranslation();
  const isLargeScreen = !!Grid.useBreakpoint()[MOBILE_BREAKPOINT_KEY];
  const isError = !!error;
  const errorData = error && "data" in error && typeof error.data === "object" && error.data !== null ? error.data : undefined;
  const errorMessage = errorData && "message" in errorData && typeof errorData.message === "string" ? errorData.message : undefined;

  return (
    <div className="flex flex-col items-center gap-6 w-full p-6 py-10 text-center">
      <div className={classNames("flex h-24 w-24 items-center justify-center rounded-full", isError ? "bg-background-danger" : "bg-background-success")}>
        {isError ? <MdError className="text-font-danger h-14 w-14" /> : <SuccessBannerIcon className="text-font-success h-14 w-14" />}
      </div>
      <div className="flex flex-col gap-1">
        <div className={classNames("text-lg font-semibold", isError ? "text-font-danger" : "text-font-active")}>
          {isError ? t("Discounts.redemptionFailed") : t("Discounts.redemptionComplete")}
        </div>
        <div className="text-font-minimal text-sm">
          {isError ? (errorMessage ?? t("Discounts.redemptionErrorFallback")) : t("Discounts.redemptionSuccessMessage")}
        </div>
      </div>
      {!isError && !isLargeScreen && (
        <InstructionCards
          usageInstructions={data?.usageInstructions}
          termsAndConditions={data?.termsAndConditions}
          title={t("Discounts.voucherGuideTitle")}
        />
      )}
      <div className="flex flex-col w-full gap-2">
        {isError ? (
          <Button type="primary" size="large" block className="bg-background-primary! font-semibold!" onClick={onRetry}>
            {t("Discounts.tryAgain")}
          </Button>
        ) : null}
        <Button size="large" block className={isError ? undefined : "bg-background-primary! font-semibold! text-white! shadow-none! border-none! outline-none!"} onClick={onClose}>
          {isError ? t("Discounts.cancel") : t("Discounts.done")}
        </Button>
      </div>
    </div>
  );
};

const DiscountsRedeemContent: FC<Props> = ({ data, imageUrl, onConfirm, onClose, loading, error, orderData, onRetry }) => {
  const { t } = useTranslation();
  const pointsRemaining = useSelector((state: RootState) => state.client.userDetails.totalPointsBalance);

  if (error || orderData) {
    return <DiscountsRedeemResult data={data} error={error} onRetry={onRetry} onClose={onClose} />;
  }

  return (
    <div className="flex w-full flex-col gap-4 p-4 py-6">
      <div className="flex items-center gap-3">
        <ImageWithEmptyFallback src={imageUrl} alt={data?.brand ?? ""} className="h-14 w-14 min-w-14 max-w-25 p-1 object-contain outline outline-border-default rounded-sm" />
        <div className="flex flex-col">
          <div className="text-font-minimal text-sm">{t("Discounts.brand")}</div>
          <div className="text-font-default text-lg font-semibold">{data?.brand}</div>
        </div>
      </div>
      <div className="divide-border-minimal flex w-full flex-col divide-y">
        <div className="flex w-full items-center justify-between py-2">
          <div className="text-font-subtle text-sm">{t("Discounts.cardValue")}</div>
          <div className="text-font-subtle text-base font-bold">
            {formatNumber(data?.totalPrice)} {data?.currency ?? t("Discounts.currencyFallback")}
          </div>
        </div>
        <div className="flex w-full items-center justify-between py-2">
          <div className="text-font-danger text-sm font-semibold">{t("Discounts.pointsSpent")}</div>
          <div className="text-font-danger text-base font-bold">
            {formatNumber(data?.points ?? 0)} {t("Discounts.pointsShort")}
          </div>
        </div>
        <div className="flex w-full items-center justify-between py-2">
          <div className="text-font-subtle text-sm">{t("Discounts.pointsRemaining")}</div>
          <div className="text-font-active text-base font-bold">
            {formatNumber((pointsRemaining ?? 0) - (data?.points ?? 0))} {t("Discounts.points")}
          </div>
        </div>
      </div>
      <Button
        type="primary"
        size="large"
        block
        className="bg-background-primary! font-semibold! p-5!"
        loading={loading}
        iconPlacement="end"
        onClick={onConfirm}
      >
        {t("Discounts.confirm")}
      </Button>
    </div>
  );
};

export default DiscountsRedeemContent;
